import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { NetworkStatus } from 'apollo-client';

import type { UIAnalyticsEventHandler, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsListener } from '@atlaskit/analytics-next';
import { Box, Flex, xcss } from '@atlaskit/primitives';

import type { BannerStateContainer } from '@confluence/banners';
import { ContentTypesHeader } from '@confluence/content-types-header';
import { ContentTypesHeaderPlaceholder } from '@confluence/content-types-header-base';
import {
	ExperienceTrackerContext,
	WHITEBOARD_EXPERIENCE,
	WHITEBOARD_VERSION_HISTORY_RESTORE_EXPERIENCE,
} from '@confluence/experience-tracker';
import { PageSegmentLoadStart } from '@confluence/browser-metrics';
import { ContentViewedEvent } from '@confluence/content-body';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils';
import { useCommentsDataActions } from '@confluence/comments-data';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { WHITEBOARD_BOARD } from '@confluence/named-routes';
import { NotFoundContained } from '@confluence/content-prerequisites';
import {
	NEW_WHITEBOARD_QUERY_PARAM,
	WHITEBOARD_IFRAME_TITLE,
	WHITEBOARD_LINKED_OBJECT,
	WHITEBOARD_LOCALE,
	WHITEBOARD_CREATION_PAYWALL_SOURCE_PREFIX,
	FOCUSED_COMMENT_ID,
	REPLY_TO_COMMENT,
	useThemeLoaded,
	useIsWhiteboardFeatureEnabled,
	CREATED_FROM_SLUG,
} from '@confluence/whiteboard-utils';
import { BannerContext } from '@confluence/banners';
import { WhiteboardPaywallReadOnlyBanner } from '@confluence/change-edition/entry-points/WhiteboardPaywallReadOnlyBanner';
import { ContentFeedbackService } from '@confluence/content-types-bridge/entry-points/content-types-feedback-service';
import { GlobalFeedbackCollectorModal } from '@confluence/content-types-bridge/entry-points/GlobalFeedbackCollectorModal';
import { ContentTypesFlagService } from '@confluence/content-types-bridge/entry-points/content-types-flag-service';
import { ContentTypeEventService } from '@confluence/content-types-bridge/entry-points/content-types-event-service';
import { useResizeBanner } from '@confluence/content-types-utils';
import { withFlags } from '@confluence/flags';
import { withDialogs } from '@confluence/dialogs';
import {
	useIsSidePanelOpen,
	useSidePanelStateActions,
} from '@confluence/side-panel-state-container';
import { ArchivedPageBanner } from '@confluence/archive-pages';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState';
import { useSkippableCoordination } from '@confluence/skippable-coordination-client';
import { fg } from '@confluence/feature-gating';
import { VersionHistoryModalManager } from '@confluence/whiteboards-onboarding';
import { VersionHistory } from '@confluence/version-history';
import { useShareNewWhiteboardFlag } from '@confluence/experiment-share-new-whiteboard/entry-points/useShareNewWhiteboardFlag';
import {
	useIsObjectSidebarShown,
	useObjectSidebarActions,
	usePanel,
} from '@confluence/object-sidebar-api';
import { ObjectSidebarPanel } from '@confluence/object-sidebar-components';
import {
	OnboardingNthUserContentTypeSpotlight,
	ContentType,
} from '@confluence/experiment-onboarding-content-type-spotlight/entry-points/OnboardingNthUserContentTypeSpotlight';
import { ContentTypesObjectSidebarService } from '@confluence/content-types-bridge';
import { useRegisterCommentsPanelCallbacks } from '@confluence/content-types-bridge/entry-points/useRegisterCommentsPanelCallbacks';

import { whiteboardShellStyle, whiteboardHeightContainer } from './whiteboard-shell-styles';
import { useWhiteboardParams } from './whiteboard-params';
import { getWhiteboardVersion } from './whiteboard-version';
import { WhiteboardExitVersion } from './WhiteboardVersion';
import { useWhiteboardBridgeAPI } from './useWhiteboardBridgeAPI';
import { WHITEBOARD_VIEW_PAGE_CONTENT_SEGMENT, StartWhiteboardPageLoad } from './perf.config';
import { useSpace } from './graphql/useSpace';
import { useWhiteboardHeaderActions } from './useWhiteboardHeaderActions';
import { MobileWebView } from './WhiteboardMobileWebView';
import { getWhiteboardSpot } from './get-whiteboard-spot';
import { WhiteboardEditionUpgradeService } from './services/whiteboard-edition-upgrade-service';
import { WhiteboardEditionUpgradeCTA } from './WhiteboardEditionUpgradeCTA';
import type { WhiteboardShellProps } from './whiteboard-shell-types';
import { useOnboardingService } from './useOnboardingService';
import { useWhiteboardQueryParamState } from './useWhiteboardQueryParamState';
import { useNavigationService } from './useNavigationService';
import { WhiteboardInternalModalService } from './services/whiteboard-internal-modal-service';
import { WhiteboardInternalModal } from './WhiteboardInternalModal';
import { WhiteboardNavigationLockout } from './WhiteboardNavigationLockout';
import { useIntegrationService } from './useIntegrationService';
import { useWhiteboardsVersionHistory } from './VersionHistory/useWhiteboardsVersionHistory';
import { PaywallStatus } from './graphql/__types__/WhiteboardUnifiedQuery';
import { useWhiteboardAIAgentService } from './useWhiteboardAIAgentService';

const PAYWALL_READ_ONLY_BANNER_ANALYTICS_SOURCE = `${WHITEBOARD_CREATION_PAYWALL_SOURCE_PREFIX}readOnlyBanner`;

const contentHeaderWrapperStyles = xcss({
	position: 'relative',
	borderBottomWidth: 'border.width',
	borderBottomStyle: 'solid',
	borderBottomColor: 'color.border',
	backgroundColor: 'elevation.surface',
});

const whiteboardShellContainerBase = xcss({
	display: 'flex',
	flexDirection: 'column',
});

const headerAndWhiteboardContainer = xcss({
	height: '100%',
	width: '100%',
	position: 'relative',
	overflow: 'hidden',
});

const archivedBannerContainerStyles = xcss({
	display: 'flex',
	position: 'absolute',
	justifyContent: 'center',
	width: '100%',
});

const archivedBannerStyles = xcss({
	transition: 'transform 300ms cubic-bezier(0.2, 0, 0, 1) 0s',
});

export const WhiteboardShellComponent = ({
	space: spaceFromProps,
	spaceKey,
	contentId,
	contentCreatorId,
	title,
	contentStatus = null,
	contentReferenceId,
	externalShareContent,
	loading,
	queryNetworkStatus,
	refetch,
	allowedFeatures,
	displayPaywallViewOnlyBanner,
	shareToken,
	flags,
	dialogs,
	dataSecurityPolicyPageExport,
	paywallStatus,
	paywallButtonClicked,
	onPermissionChange,
}: WhiteboardShellProps) => {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);
	const { isNotLoaded, onThemeLoaded } = useThemeLoaded(isSSR);
	const whiteboardsVersionHistoryService = useWhiteboardsVersionHistory();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();
	const { hideObjectSidebar } = useObjectSidebarActions();
	useRegisterCommentsPanelCallbacks();
	const navigationService = useNavigationService(spaceKey);

	// Used to clear existing comments data when we leave the space overview page
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { clearCommentsData } = useCommentsDataActions();

	// refs and resize observers needed only to properly position side panel since content types header is absolutely positioned
	const {
		bannerHeight: whiteboardReadOnlyBannerHeight,
		setBannerElement: setWhiteboardReadOnlyBannerElement,
	} = useResizeBanner(isSSR);

	const {
		bannerHeight: whiteboardHeaderHeight,
		setBannerElement: setWhiteboardHeaderElement,
		setBannerHeight: setWhiteboardHeaderHeight,
	} = useResizeBanner(isSSR);
	const handleHeightChange = (height: number) => {
		setWhiteboardHeaderHeight(height);
	};

	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isWhiteboardHeaderPinned = isWhiteboardFeatureEnabled('whiteboardSolidHeader');

	const isLoadingNewContent = loading && queryNetworkStatus !== NetworkStatus.refetch;
	const params = useWhiteboardParams();
	const version = getWhiteboardVersion(params);
	const serverSpot = getWhiteboardSpot(params, 'canvas-server');
	const relaySpot = getWhiteboardSpot(params, 'canvas-relay');

	const shouldShowMobileWebWhiteboard = shouldShowMobileWeb() && !params.isMobileAppEmbed;
	const contentActions = useWhiteboardHeaderActions();

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { includeTopNav } = useContext(BannerContext);

	const [editionUpgradeService] = useState(() => new WhiteboardEditionUpgradeService());

	const [feedbackService] = useState(() => new ContentFeedbackService());

	const [eventService] = useState(() => new ContentTypeEventService());
	const [whiteboardInternalModalService] = useState(() => new WhiteboardInternalModalService());

	const [flagService] = useState(() => new ContentTypesFlagService(flags));

	const [showVersionHistorySpotlight, setShowVersionHistorySpotlight] = useState<boolean>(false);

	const whiteboardContainerRef = useRef<HTMLDivElement>(null);

	// When paywall modal is present, we want to track how users interact with it so that
	// the EP message runs as soon as they close the modal
	// This also ensures that onboarding modal does not get prematurely exposed when paywall is present
	const shouldStartVersionHistoryOnboardingExperience = (() => {
		if (!paywallButtonClicked && paywallStatus === PaywallStatus.ACTIVE) {
			return true;
		} else if (paywallButtonClicked === 'close') {
			return true;
		} else if (paywallButtonClicked === 'viewOnly') {
			if (paywallStatus && paywallStatus !== PaywallStatus.UNSET) {
				return true;
			}
		} else if (paywallButtonClicked === 'active') {
			if (paywallStatus && paywallStatus === PaywallStatus.ACTIVE) {
				return true;
			}
		} else if (paywallButtonClicked === 'redirect') {
			return true;
		}
		return false;
	})();

	const isNotPublicWhiteboard = !Boolean(externalShareContent);

	const [hasStartedVersionHistoryOnboardingCoordination, stopVersionHistoryOnboardingCoordination] =
		useSkippableCoordination(
			'cc-whiteboard-version-history-onboarding',
			!(isNotPublicWhiteboard && shouldStartVersionHistoryOnboardingExperience),
		);

	const onCloseVersionHistoryModal = useCallback(() => {
		void stopVersionHistoryOnboardingCoordination();
	}, [stopVersionHistoryOnboardingCoordination]);

	const showVersionHistoryOnboardingModal =
		shouldStartVersionHistoryOnboardingExperience &&
		hasStartedVersionHistoryOnboardingCoordination &&
		!Boolean(externalShareContent);

	const isSidePanelOpen = useIsSidePanelOpen();
	const { hideSidePanel } = useSidePanelStateActions();

	const onConfirm = useCallback(() => {
		setShowVersionHistorySpotlight(true);
	}, [setShowVersionHistorySpotlight]);

	const space = useSpace(spaceKey, spaceFromProps);
	const integrationService = useIntegrationService(flags, contentId, contentStatus);
	const routeDataRef = useRouteDataRef();

	const updateWhiteboardHeaderEvents: UIAnalyticsEventHandler = (event: UIAnalyticsEvent) => {
		const newData = {
			objectType: 'whiteboard',
		};

		const newAttributes = {
			contentType: 'whiteboard',
		};

		event.update((payload) => {
			const mergedAttributes = {
				...payload.data?.attributes,
				...newAttributes,
			};

			return {
				...payload,
				data: {
					...payload.data,
					...newData,
					attributes: mergedAttributes,
				},
			};
		});
	};

	const isNewWhiteboard = Boolean(
		useWhiteboardQueryParamState(contentId, NEW_WHITEBOARD_QUERY_PARAM),
	);
	useShareNewWhiteboardFlag({
		isNewWhiteboard,
		isMobile: shouldShowMobileWebWhiteboard || params.isMobileAppEmbed,
		contentId,
		spaceKey,
		flags,
		dialogs,
	});

	const aiAgentService = useWhiteboardAIAgentService({ contentId, spaceKey, contentTitle: title });
	useEffect(() => {
		aiAgentService.setWhiteboardContainerRef(whiteboardContainerRef);
	}, [aiAgentService, whiteboardContainerRef]);

	const [objectSidebarService] = useState(() => new ContentTypesObjectSidebarService());

	const onboardingService = useOnboardingService({ isNewWhiteboard });

	// When version history panel is open, we pass in 'latest' value to versionId query param
	// to let canvas know that the current version should be in view only mode
	const whiteboardVersionId =
		isSidePanelOpen && !whiteboardsVersionHistoryService.versionId
			? 'latest'
			: whiteboardsVersionHistoryService.versionId;

	const onPermissionChangeHandler = useCallback(
		(permissions: string[]) => {
			aiAgentService.updatePermissions(permissions);

			if (onPermissionChange) {
				onPermissionChange(permissions);
			}
		},
		[onPermissionChange, aiAgentService],
	);

	useEffect(() => {
		return () => {
			if (fg('cc-whiteboards-consolidating-comments')) {
				resetInitialDataLoadedMap();
				clearCommentsData();
			}
		};
	}, [clearCommentsData, resetInitialDataLoadedMap, contentId]);

	useEffect(() => {
		// hide sidebar when navigating between different whiteboards
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		hideObjectSidebar();
	}, [contentId, hideObjectSidebar]);

	useEffect(() => {
		// hide the object sidebar panels when we open version history.
		// when we have the version history panel open, the sidebar control is not visible,
		// so we will never run into the case where we have version history open and then the user
		// opens the comments/details panel
		if (isObjectSidebarShown && isSidePanelOpen) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			hideObjectSidebar();
		}
	}, [isObjectSidebarShown, isSidePanelOpen, hideObjectSidebar]);

	const { initialUrl } = useWhiteboardBridgeAPI({
		version,
		serverSpot,
		relaySpot,
		space,
		spaceKey,
		contentId,
		// use "undefined" value here allows us to render whiteboard skeleton loading state
		// while WhiteboardUnifiedQuery is loading
		referenceId: isLoadingNewContent ? undefined : contentReferenceId,
		contentCreatorId: isLoadingNewContent ? undefined : contentCreatorId,
		shareToken,
		params,
		dataSecurityPolicyPageExport,
		refetch,
		whiteboardVersionId,
		services: {
			aiAgentService,
			editionUpgradeService,
			feedbackService,
			onboardingService,
			whiteboardInternalModalService,
			eventService,
			flagService,
			integrationService,
			whiteboardsVersionHistoryService,
			navigationService,
			objectSidebarService,
		},
		whiteboardLocale: params[WHITEBOARD_LOCALE],
		whiteboardLinkedObject: params[WHITEBOARD_LINKED_OBJECT],
		focusedCommentId: params[FOCUSED_COMMENT_ID],
		replyToComment: params[REPLY_TO_COMMENT],
		createdFromSlug: params[CREATED_FROM_SLUG],
		onThemeLoaded,
		onPermissionChange: onPermissionChangeHandler,
	});

	useEffect(() => {
		const flowQueryParam = routeDataRef.current?.queryParams?.flow;

		if (flowQueryParam === 'restoreVersion') {
			experienceTracker.succeed({
				name: WHITEBOARD_VERSION_HISTORY_RESTORE_EXPERIENCE,
			});
		}
	}, [routeDataRef, experienceTracker, contentId]);

	useEffect(() => {
		eventService.registerEventHandler('hideSidePanel', () => {
			hideSidePanel();
		});
		return () => {
			eventService.unregisterEventHandler('hideSidePanel');
		};
	}, [eventService, hideSidePanel]);

	useEffect(() => {
		objectSidebarService.onChangeObjectSidebarShown({
			isOpen: isObjectSidebarShown,
			panelId: panel?.id,
		});
	}, [isObjectSidebarShown, objectSidebarService, panel?.id]);

	useEffect(() => {
		whiteboardsVersionHistoryService.setWhiteboardVersion(null);
		hideSidePanel();
		// If we include the version history service here
		// we end up closing the side panel when selecting a new version
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, hideSidePanel]);

	useEffect(() => {
		return () => {
			hideSidePanel();
		};
	}, [hideSidePanel]);

	const handleCloseTitleSpotlight = useCallback(() => {
		onboardingService.startOnboarding('spotlight-sticky-creator');
	}, [onboardingService]);

	const handleRegisterOnShowHeaderSpotlight = useCallback(
		(cb: () => void) => {
			onboardingService.onShowHeaderSpotlight(cb);
		},
		[onboardingService],
	);

	const handleRegisterOnShowTitleSpotlight = useCallback(
		(cb: () => void) => {
			onboardingService.onShowTitleSpotlight(cb);
		},
		[onboardingService],
	);

	const handleRegisterOnShowCollaboratorSpotlight = useCallback(
		(cb: () => void) => {
			onboardingService.onShowCollaboratorSpotlight(cb);
		},
		[onboardingService],
	);

	const renderArchiveBanner = () => {
		return (
			<Box
				xcss={archivedBannerContainerStyles}
				style={{ marginTop: isWhiteboardHeaderPinned ? '100px' : '60px' }}
			>
				<Box
					xcss={archivedBannerStyles}
					style={{
						transform: isSidePanelOpen ? `translateX(-160px)` : `translateX(0)`,
					}}
				>
					<ArchivedPageBanner contentId={contentId} marginStyle="none" />
				</Box>
			</Box>
		);
	};

	if (!loading && !contentReferenceId) {
		experienceTracker.fail({
			name: WHITEBOARD_EXPERIENCE,
			error: new Error('WhiteboardUnifiedQuery did not return valid referenceId'),
		});

		return <NotFoundContained route={WHITEBOARD_BOARD} spaceKey={spaceKey} />;
	}

	// Always show the Object Header if enabled by the feature gate - but keep the current behavior
	// for the old content header which isn't shown in SSR
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isHeaderShown = !process.env.REACT_SSR || fg('confluence_frontend_object_header');
	const showContentTypesHeader = isHeaderShown && !isLoadingNewContent && !params.isSmartLinkEmbed;

	const showContentTypesHeaderPlaceholder = isLoadingNewContent && !params.isSmartLinkEmbed;

	return (
		<>
			<StartWhiteboardPageLoad contentId={contentId} />
			<Subscribe to={whiteboardHeightContainer}>
				{(bannerState: BannerStateContainer) => {
					// need to use bannerState to account for topnav in public links
					const bannerHeight = params.isMobileAppEmbed
						? 0
						: bannerState.getTotalHeight(includeTopNav);

					return (
						<Box
							ref={whiteboardContainerRef}
							xcss={whiteboardShellContainerBase}
							style={{ height: bannerHeight > 0 ? `calc(100vh - ${bannerHeight}px)` : '100vh' }}
							testId="whiteboard-shell-container"
						>
							{displayPaywallViewOnlyBanner && (
								<WhiteboardPaywallReadOnlyBanner
									ref={setWhiteboardReadOnlyBannerElement}
									analyticsSource={PAYWALL_READ_ONLY_BANNER_ANALYTICS_SOURCE}
								/>
							)}
							<Flex direction="column" xcss={headerAndWhiteboardContainer}>
								{showContentTypesHeaderPlaceholder && (
									<ContentTypesHeaderPlaceholder isFloating={!isWhiteboardHeaderPinned} />
								)}
								{showContentTypesHeader && (
									<AnalyticsListener onEvent={updateWhiteboardHeaderEvents}>
										{isWhiteboardHeaderPinned ? (
											<Box
												testId="pinned-content-header"
												xcss={contentHeaderWrapperStyles}
												ref={setWhiteboardHeaderElement}
											>
												<ContentTypesHeader
													contentId={contentId}
													contentActions={contentActions}
													externalShareContent={externalShareContent}
													allowedFeatures={allowedFeatures}
													isFloating={!isWhiteboardHeaderPinned}
													onCloseTitleSpotlight={handleCloseTitleSpotlight}
													registerShowTitleSpotlightHandler={handleRegisterOnShowTitleSpotlight}
													handleRegisterOnShowHeaderSpotlight={handleRegisterOnShowHeaderSpotlight}
													handleRegisterOnShowCollaboratorSpotlight={
														handleRegisterOnShowCollaboratorSpotlight
													}
													isHeaderPinned={isWhiteboardHeaderPinned}
													showVersionHistorySpotlight={showVersionHistorySpotlight}
													setShowVersionHistorySpotlight={setShowVersionHistorySpotlight}
													whiteboardAIAgentService={aiAgentService}
												/>
											</Box>
										) : (
											<ContentTypesHeader
												contentId={contentId}
												contentActions={contentActions}
												externalShareContent={externalShareContent}
												allowedFeatures={allowedFeatures}
												isFloating={!isWhiteboardHeaderPinned}
												onCloseTitleSpotlight={handleCloseTitleSpotlight}
												registerShowTitleSpotlightHandler={handleRegisterOnShowTitleSpotlight}
												handleRegisterOnShowHeaderSpotlight={handleRegisterOnShowHeaderSpotlight}
												handleRegisterOnShowCollaboratorSpotlight={
													handleRegisterOnShowCollaboratorSpotlight
												}
												onHeightChange={handleHeightChange}
												showVersionHistorySpotlight={showVersionHistorySpotlight}
												setShowVersionHistorySpotlight={setShowVersionHistorySpotlight}
												whiteboardAIAgentService={aiAgentService}
											/>
										)}
									</AnalyticsListener>
								)}
								{renderArchiveBanner()}
								{shouldShowMobileWebWhiteboard ? (
									<MobileWebView bannerState={bannerState} />
								) : (
									<>
										<PageSegmentLoadStart metric={WHITEBOARD_VIEW_PAGE_CONTENT_SEGMENT} />
										<iframe
											title={WHITEBOARD_IFRAME_TITLE}
											// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
											style={whiteboardShellStyle(isNotLoaded, isSidePanelOpen)}
											src={initialUrl}
											frameBorder={0}
										/>
										<OnboardingNthUserContentTypeSpotlight
											contentType={ContentType.WHITEBOARD}
											contentId={contentId}
										/>
										{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
										{!process.env.REACT_SSR && <ContentViewedEvent contentId={contentId} />}
									</>
								)}
								{isObjectSidebarShown &&
									['detailsPanel', 'commentsPanel'].includes(panel?.id ?? '') &&
									fg('confluence_frontend_whiteboards_content_wrapper') && (
										<ObjectSidebarPanel useFloatingSidebarHeight useFloatingSidebar />
									)}
								{isSidePanelOpen && (
									<VersionHistory
										headerHeight={
											bannerHeight + whiteboardReadOnlyBannerHeight + whiteboardHeaderHeight
										}
										contentId={contentId}
										title={title}
										displayTopBorder={!isWhiteboardHeaderPinned}
										panelTop={whiteboardHeaderHeight}
										eventService={eventService}
										setVersion={whiteboardsVersionHistoryService.setWhiteboardVersion}
										contentType="whiteboard"
									/>
								)}
							</Flex>
						</Box>
					);
				}}
			</Subscribe>
			<WhiteboardEditionUpgradeCTA editionUpgradeService={editionUpgradeService} />
			<GlobalFeedbackCollectorModal feedbackService={feedbackService} />
			<WhiteboardInternalModal whiteboardInternalModalService={whiteboardInternalModalService} />
			<WhiteboardExitVersion version={version} serverSpot={serverSpot} relaySpot={relaySpot} />
			{showVersionHistoryOnboardingModal && (
				<VersionHistoryModalManager onClose={onCloseVersionHistoryModal} onConfirm={onConfirm} />
			)}
			<WhiteboardNavigationLockout navigationService={navigationService} contentId={contentId} />
		</>
	);
};

export const WhiteboardShell = withDialogs(withFlags(WhiteboardShellComponent));
